import React, { useMemo } from 'react';
import { Template } from 'devextreme-react';
import DataGrid, { Column, Paging, SearchPanel } from 'devextreme-react/data-grid';
import {
  Grid, Link, useMediaQuery, useTheme,
} from '@material-ui/core';
import {
  useTranslation,
  useMaestroDataSource,
} from '@michelin/central-provider';
import CreatedByAvatar from 'components/CreatedByAvatar';
import {
  Button,
  FilterGroup,
  SelectOption,
  useListSelectFilter,
} from '@michelin/acid-components';
import { serviceAreasEligibleSTList } from '../../../Types/APITypes';
import { CONTENT_AREA_HEIGHT } from '../../../Utils';
import { TechServiceAreaLocation } from '../types';
import { RenderCity } from '../Components/TableColumns';

interface SelLocationsListTableProps {
  stNumber?: string;
  btNumber: string;
  isPrimary: boolean;
  primaryLocations: string[];
  secondaryLocations: string[];
  onSelectPrim: (data: TechServiceAreaLocation) => void;
  onSelectSec: (data: { added: boolean; st: string }) => void;
}

export function SelLocationsListTable(props: SelLocationsListTableProps) {
  const {
    stNumber,
    btNumber,
    isPrimary,
    primaryLocations,
    secondaryLocations,
    onSelectPrim,
    onSelectSec,
  } = props;

  const { t } = useTranslation();
  const theme = useTheme();
  const tabletView = useMediaQuery(theme.breakpoints.down(800));
  const cellView = useMediaQuery(theme.breakpoints.down(500));

  const countryOptions: SelectOption[] = [
    {
      label: t('United States'),
      value: 'US',
    },
    {
      label: t('Canada'),
      value: 'CA',
    },
  ];
  const countryFilter = useListSelectFilter({
    label: t('Country'),
    options: countryOptions,
    unselectedLabel: t('Any Country'),
  });
  const filterGroup = useMemo(
    () => new FilterGroup([countryFilter.filter]),
    [countryFilter.filter],
  );

  const ds = useMaestroDataSource({
    API: serviceAreasEligibleSTList(btNumber),
    reqOptions: {
      filters: {
        country: countryFilter.value,
        ST: stNumber || '',
      },
    },
    componentPlacements: {
      filtersPlacement: tabletView ? 'outside' : 'embeded',
      searchPlacement: cellView ? 'outside' : 'embeded',
    },
    filterGroup,
  });

  /// //////////////////////////////////////////////////////////////////
  /// COLUMN RENDER METHODS
  /// //////////////////////////////////////////////////////////////////

  const ShipToInfo = (loc: TechServiceAreaLocation): JSX.Element => (
    <Grid container alignItems="center" key={`shiptoinfo-${loc.ship_to_number}`}>
      <Grid item>
        <CreatedByAvatar
          customerType="ST"
          customerNumber={loc.ship_to_number}
        />
      </Grid>
      <Grid item>
        <div style={{ minWidth: 170, paddingLeft: 8 }}>
          <Link component="span" underline="none" style={{ cursor: 'default' }}>
            <b>{loc.customer_dba_name || loc.customer_name || ''}</b>
          </Link>
          {loc.ship_to_number && (
            <div>
              {`${t('Ship to #')} `}
              <b>{loc.ship_to_number}</b>
            </div>
          )}
          {loc.extrnl_cust_id && (
            <div>
              {`${t('Store #')} `}
              <b>{loc.extrnl_cust_id}</b>
            </div>
          )}
        </div>
      </Grid>
    </Grid>
  );

  function renderAssignPrim(
    row: TechServiceAreaLocation,
    locations: string[],
    setPrimary: (data: any) => void,
  ) {
    const selectedLocation = locations.includes(row.ship_to_number);
    return (
      <Button
        variant="default"
        onClick={() => {
          setPrimary(row);
        }}
        disabled={selectedLocation}
        key={`renderAssignPrim-${row.ship_to_number}`}
      >
        {selectedLocation ? t('Selected') : t('Select')}
      </Button>
    );
  }
  function renderAssignSec(
    row: TechServiceAreaLocation,
    locations: string[],
    st: string,
    setLoc: (data: { added: boolean; st: string }) => void,
  ) {
    const selectedLocation = locations.includes(row.ship_to_number);
    const isPrimaryLocation = row.ship_to_number === st;
    if (isPrimaryLocation) {
      return (
        <Button variant="default" disabled key={`renderAssignSec-${row.ship_to_number}`}>
          {t('Primary ST')}
        </Button>
      );
    }
    return (
      <Button
        variant="default"
        onClick={() => {
          setLoc({ added: !selectedLocation, st: row.ship_to_number });
        }}
        key={`renderAssignSec-${row.ship_to_number}`}
      >
        {selectedLocation ? t('Remove') : t('Add')}
      </Button>
    );
  }

  return (
    <>
      <div>
        {tabletView ? ds.renderFilterFields(null, { margin: 10 }) : undefined}
        {cellView ? (
          <div style={{ marginBottom: -10, textAlign: 'right' }}>
            {ds.renderResultsCounter()}
          </div>
        ) : undefined}
      </div>
      <DataGrid
        style={{ height: CONTENT_AREA_HEIGHT }}
        noDataText={t('No locations found...')}
        scrolling={{ mode: 'virtual', rowRenderingMode: 'standard' }}
        className="freespaced-table"
        dataSource={ds.dataSource}
        allowColumnReordering
        allowColumnResizing
        wordWrapEnabled
        remoteOperations
        columnAutoWidth
        onToolbarPreparing={ds.onToolbarPreparing}
      >
        <Paging defaultPageSize={50} />
        <Template name="filterFields" render={ds.renderFilterFields} />
        <Template name="resultsCounter" render={ds.renderResultsCounter} />
        <SearchPanel visible highlightSearchText={false} />
        <Column
          caption={t('Location')}
          cellRender={({ row }) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <ShipToInfo {...row.data} />
          )}
          dataField="ship_to_number"
          defaultSortOrder="asc"
          allowSorting
        />
        <Column
          caption={t('Location Address')}
          cellRender={({ row }) => <RenderCity loc={row.data} />}
          dataField="address"
          allowSorting={false}
        />
        {isPrimary && (
          <Column
            key="assign_column"
            allowFiltering={false}
            allowSorting={false}
            caption={t('Assign')}
            alignment="center"
            dataField="assign"
            cellRender={({ row }) => renderAssignPrim(row.data, primaryLocations, (data: any) => onSelectPrim(data))}
          />
        )}
        {!isPrimary && stNumber && (
          <Column
            key="assign_column"
            allowFiltering={false}
            allowSorting={false}
            caption={t('Assign')}
            alignment="center"
            dataField="assign"
            cellRender={({ row }) => renderAssignSec(
              row.data,
              secondaryLocations,
              stNumber,
              (data: any) => onSelectSec(data),
            )}
          />
        )}
      </DataGrid>
    </>
  );
}
